import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

interface CacheEntry {
  blobUrl: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class ImageCacheService {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private readonly http = inject(HttpClient);
  private loadingMap = new Map<string, Observable<string>>();

  /**
   * Get image as blob URL with caching
   */
  getImage(url: string): Observable<string> {
    // Check if cached and not expired
    const cached = this.cache.get(url);
    if (cached) {
      const now = Date.now();
      const isExpired = now - cached.timestamp > this.CACHE_DURATION;

      if (!isExpired) {
        return of(cached.blobUrl);
      } else {
        // Revoke expired blob URL to free memory
        URL.revokeObjectURL(cached.blobUrl);
        this.cache.delete(url);
      }
    }

    // Check if already loading
    const loading = this.loadingMap.get(url);
    if (loading) {
      return loading;
    }

    // Load image
    const request$ = this.http.get(url, { responseType: 'blob' }).pipe(
      map(blob => {
        const blobUrl = URL.createObjectURL(blob);
        this.cache.set(url, {
          blobUrl,
          timestamp: Date.now(),
        });
        this.loadingMap.delete(url);
        return blobUrl;
      }),
      catchError(error => {
        console.warn(`Failed to load image: ${url}`, error);
        this.loadingMap.delete(url);
        // Return the original URL as fallback
        return of(url);
      })
    );

    this.loadingMap.set(url, request$);
    return request$;
  }

  /**
   * Clear all cached images
   */
  clear(): void {
    // Revoke all blob URLs to free memory
    this.cache.forEach(entry => {
      URL.revokeObjectURL(entry.blobUrl);
    });
    this.cache.clear();
    this.loadingMap.clear();
  }

  /**
   * Clean up expired entries
   */
  cleanupExpired(): void {
    const now = Date.now();
    const toDelete: string[] = [];

    this.cache.forEach((entry, url) => {
      if (now - entry.timestamp > this.CACHE_DURATION) {
        URL.revokeObjectURL(entry.blobUrl);
        toDelete.push(url);
      }
    });

    toDelete.forEach(url => this.cache.delete(url));
  }

  /**
   * Start automatic cleanup of expired entries
   * Should be called once during app initialization
   */
  startAutoCleanup(): void {
    if (typeof window !== 'undefined') {
      setInterval(
        () => {
          this.cleanupExpired();
        },
        2 * 60 * 1000
      ); // Every 2 minutes
    }
  }
}
